import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../features/user-dashboard/auth/AuthContext';
import adminService, { type WhatsNewAdminItem } from '../services/adminService';
import type { User } from '../types/auth';

function isAdminUser(u: User | null): boolean {
  if (!u) return false;
  return u.is_superuser === true || u.role === 'admin';
}

function formatDt(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

type FormState = {
  title: string;
  version_label: string;
  bullets: string[];
};

const emptyForm = (): FormState => ({
  title: '',
  version_label: '',
  bullets: [''],
});

export default function AdminWhatsNewPage() {
  const navigate = useNavigate();
  const { user: me, loading: authLoading } = useAuth();
  const [items, setItems] = useState<WhatsNewAdminItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const load = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const data = await adminService.listWhatsNew();
      setItems(data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to load What’s New';
      setErr(String(msg));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdminUser(me)) return;
    void load();
  }, [authLoading, me, load]);

  useEffect(() => {
    if (authLoading) return;
    if (me && !isAdminUser(me)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, me, navigate]);

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOk('');
    setErr('');
  };

  const startEdit = (item: WhatsNewAdminItem) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      version_label: item.version_label || '',
      bullets: item.bullets.length ? [...item.bullets] : [''],
    });
    setOk('');
    setErr('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setBullet = (index: number, value: string) => {
    setForm((prev) => {
      const bullets = [...prev.bullets];
      bullets[index] = value;
      return { ...prev, bullets };
    });
  };

  const addBullet = () => {
    setForm((prev) => ({ ...prev, bullets: [...prev.bullets, ''] }));
  };

  const removeBullet = (index: number) => {
    setForm((prev) => {
      const bullets = prev.bullets.filter((_, i) => i !== index);
      return { ...prev, bullets: bullets.length ? bullets : [''] };
    });
  };

  const cleanedBullets = () =>
    form.bullets.map((b) => b.trim()).filter(Boolean);

  const saveDraft = async () => {
    setErr('');
    setOk('');
    const title = form.title.trim();
    if (!title) {
      setErr('Please enter a title.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        title,
        version_label: form.version_label.trim() || null,
        bullets: cleanedBullets(),
        status: 'draft' as const,
      };
      if (editingId) {
        await adminService.updateWhatsNew(editingId, body);
        setOk('Draft saved.');
      } else {
        const created = await adminService.createWhatsNew(body);
        setEditingId(created.id);
        setOk('Draft created.');
      }
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not save draft';
      setErr(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    setErr('');
    setOk('');
    const title = form.title.trim();
    const bullets = cleanedBullets();
    if (!title) {
      setErr('Please enter a title.');
      return;
    }
    if (!bullets.length) {
      setErr('Add at least one bullet point before publishing.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        title,
        version_label: form.version_label.trim() || null,
        bullets,
      };
      let id = editingId;
      if (id) {
        await adminService.updateWhatsNew(id, body);
      } else {
        const created = await adminService.createWhatsNew({ ...body, status: 'draft' });
        id = created.id;
        setEditingId(id);
      }
      await adminService.publishWhatsNew(id);
      setOk('Published. Logged-in users who have not seen this update will see it after login.');
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not publish';
      setErr(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const unpublish = async (id: string) => {
    setErr('');
    setOk('');
    setSaving(true);
    try {
      await adminService.unpublishWhatsNew(id);
      setOk('Unpublished. Users will no longer see this update.');
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not unpublish';
      setErr(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this What’s New entry? This cannot be undone.')) return;
    setErr('');
    setOk('');
    setSaving(true);
    try {
      await adminService.deleteWhatsNew(id);
      if (editingId === id) startNew();
      setOk('Deleted.');
      await load();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not delete';
      setErr(String(msg));
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || (me && !isAdminUser(me))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <AdminLayout title="What’s New">
      <p className="text-sm text-gray-600 mb-4">
        Write release notes for users after login — like Play Store “What’s new”. No coding needed:
        add a title and bullet points, then publish.
      </p>

      {err && (
        <div className="mb-3 p-3 rounded-lg bg-amber-50 text-amber-900 border text-sm">{err}</div>
      )}
      {ok && (
        <div className="mb-3 p-3 rounded-lg bg-emerald-50 text-emerald-900 border text-sm">{ok}</div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow p-4 sm:p-5 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="text-base font-semibold text-gray-900">
            {editingId ? 'Edit update' : 'New update'}
          </h2>
          {editingId && (
            <button
              type="button"
              onClick={startNew}
              className="text-sm text-emerald-800 hover:underline"
            >
              Start a new update instead
            </button>
          )}
        </div>

        <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
          placeholder="e.g. August 2026 update"
          className="w-full border rounded-md px-3 py-2 text-sm mb-3"
          maxLength={255}
        />

        <label className="block text-xs font-medium text-gray-600 mb-1">
          Version label <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          value={form.version_label}
          onChange={(e) => setForm((p) => ({ ...p, version_label: e.target.value }))}
          placeholder="e.g. v2.4"
          className="w-full border rounded-md px-3 py-2 text-sm mb-3 max-w-xs"
          maxLength={64}
        />

        <div className="mb-1 flex items-center justify-between">
          <label className="block text-xs font-medium text-gray-600">What’s new (bullet points)</label>
          <button
            type="button"
            onClick={addBullet}
            className="text-xs font-medium text-emerald-800 hover:underline"
          >
            + Add bullet
          </button>
        </div>
        <div className="space-y-2 mb-4">
          {form.bullets.map((b, i) => (
            <div key={i} className="flex gap-2">
              <span className="text-gray-400 pt-2 text-sm">•</span>
              <input
                type="text"
                value={b}
                onChange={(e) => setBullet(i, e.target.value)}
                placeholder={
                  i === 0
                    ? 'e.g. Pest alerts for your fields'
                    : i === 1
                      ? 'e.g. Vegetation health alerts'
                      : 'Another improvement…'
                }
                className="flex-1 border rounded-md px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => removeBullet(i)}
                className="px-2 text-gray-500 hover:text-red-600 text-sm"
                aria-label="Remove bullet"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => void saveDraft()}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            Save draft
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void publish()}
            className="px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-50"
          >
            Publish
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">All updates</h2>
          {loading && <span className="text-xs text-gray-500">Loading…</span>}
        </div>
        {items.length === 0 && !loading ? (
          <p className="p-4 text-sm text-gray-500">No updates yet. Create one above.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((item) => (
              <li key={item.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900">{item.title}</h3>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          item.status === 'published'
                            ? 'bg-emerald-100 text-emerald-900'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {item.status === 'published' ? 'Published' : 'Draft'}
                      </span>
                      {item.version_label && (
                        <span className="text-xs text-gray-500">{item.version_label}</span>
                      )}
                    </div>
                    <ul className="text-sm text-gray-600 list-disc pl-5 space-y-0.5 mb-2">
                      {item.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                      {!item.bullets.length && (
                        <li className="list-none -ml-5 text-gray-400">No bullets yet</li>
                      )}
                    </ul>
                    <p className="text-xs text-gray-400">
                      Updated {formatDt(item.updated_at)}
                      {item.published_at ? ` · Published ${formatDt(item.published_at)}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => startEdit(item)}
                      className="px-3 py-1.5 rounded-md border text-sm hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    {item.status === 'published' ? (
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => void unpublish(item.id)}
                        className="px-3 py-1.5 rounded-md border text-sm hover:bg-gray-50 disabled:opacity-50"
                      >
                        Unpublish
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={saving || !item.bullets.length}
                        onClick={() => void adminService.publishWhatsNew(item.id).then(load)}
                        className="px-3 py-1.5 rounded-md bg-emerald-700 text-white text-sm hover:bg-emerald-800 disabled:opacity-50"
                      >
                        Publish
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void remove(item.id)}
                      className="px-3 py-1.5 rounded-md border border-red-200 text-red-700 text-sm hover:bg-red-50 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AdminLayout>
  );
}
